<template>
  <div class="echart bg-white rounded-lg shadow-lg h-full" v-loading="loading">
    <div class="barChart">
      <vue-echarts
        v-if="optionData"
        :option="options"
        style="height: 700px; width: 100%;"
        :auto-resize="true"
        ref="chart"
      />
    </div>
  </div>
</template>

<script>
import { VueEcharts } from 'vue3-echarts'
import * as echarts from 'echarts'

export default {
  name: 'CompanyBarChart',
  components: { VueEcharts },
  props: {
    optionData: {
      type: Object,
      default: () => ({ title: '', data: [], total: null })
    },
    loading: {
      type: Boolean,
      default: false
    }
  },
  data() {
    return {
      chartWidth: 0
    }
  },
  mounted() {
    this.updateChartWidth()
    window.addEventListener('resize', this.updateChartWidth)
  },
  beforeUnmount() {
    window.removeEventListener('resize', this.updateChartWidth)
  },
  methods: {
    updateChartWidth() {
      this.$nextTick(() => {
        const chartEl = this.$refs.chart?.$el
        if (chartEl) {
          this.chartWidth = chartEl.clientWidth
        }
      })
    }
  },
  computed: {
    options() {
      const isLossChart = this.optionData.title.includes('Loss Breakdown')
      const dataItems = this.optionData.data
      // use the parent‐provided total for percentage
      const total = Number(this.optionData.total ?? 0)

      const categoryLabels = dataItems.map(i => i.name)

      const estimatedCharWidth = 12
      const totalLabelWidth = categoryLabels.reduce((sum, label) => sum + label.length * estimatedCharWidth, 0)
      const shouldRotate = totalLabelWidth > this.chartWidth

      if (isLossChart) {
        const rawValues = dataItems.map(i => Number(i.value))
        // find the max abs for symmetric axis
        const maxRaw    = Math.max(...rawValues.map(v => Math.abs(v)),0)
        const mag       = 10 ** Math.floor(Math.log10(maxRaw))
        const step      = mag/2
        const ratio     = maxRaw/mag
        const topBnd    = ratio<=2 ? 2*mag : (Math.ceil(ratio)+1)*mag

        // gradient
        const grd = new echarts.graphic.LinearGradient(0,0,0,1,[
          { offset:0,   color:'#A4DEFF' },
          { offset:0.5, color:'#3AADEE' },
          { offset:1,   color:'#026BA7' },
        ])

        // flip sign: loss (positive raw → go DOWN), gain (negative raw → go UP)
        const seriesData = dataItems.map(i => {
          const raw = Number(i.value)
          return {
            value: -raw,        // flip sign
            original: raw,      // keep for labels
            itemStyle: {
              borderRadius: raw>=0 ? [0,0,8,8] : [8,8,0,0]
            }
          }
        })

        return {
          backgroundColor:'#fff',
          title: {
            text: 'Gain/Loss Breakdown',
            left:'center', top:'3%',
            textStyle:{ color:'#2272FF', fontSize:24, fontWeight:500 }
          },
          grid: { top:80, left:40, right:40, bottom:20, containLabel:true },
          tooltip: { trigger:'axis', axisPointer:{ type:'shadow' } },
          xAxis: {
            type:'category',
            data: dataItems.map(i=>i.name),
            axisLabel: { margin: 10, rotate: shouldRotate ? 45 : 0 , interval: 0},
            axisLine:{ lineStyle:{ color:'#666' }},
            axisTick:{ alignWithLabel:true }
          },
          yAxis: {
            type:'value',
            min:  -topBnd,
            max:   topBnd,
            interval: step,
            axisLine:  { show:false },
            splitLine: { lineStyle:{ color:'#eee' }},
            axisLabel:{ formatter: v => Math.abs(v) },
          },
          series:[{
            type:'bar',
            data: seriesData,
            barMaxWidth:'40%',
            itemStyle:{ color:grd },
            label: {
              show:true,
              position:'top',
              formatter: ({ data }) => {
                const abs  = Math.abs(data.original).toFixed(3)
                const pct  = total>0 
                           ? (Math.abs(data.original/total)*100).toFixed(2)
                           : '0.00'
                return `{valueStyle|${abs}}\n{percentStyle|(${pct}%)}`
              },
              rich: {
                valueStyle:   { fontSize:14, color:'#444', align:'center' },
                percentStyle: { fontSize:10, color:'#888', align:'center' }
              }
            }
          }],
          graphic: [
          {
            type: 'text',
            left:  '1%',     
            rotation: Math.PI / 2,
            top:   '25%',    
            style: {
              text: 'Gain',
              fill: '#888',
              fontSize: 20,
              align: 'center',
              fill: 'green',
            }
          },
          {
            type: 'text',
            left:  '1%',
            rotation: Math.PI / 2,
            top:   '75%',   
            style: {
              text: 'Loss',
              fill: '#888',
              fontSize: 20,
              align: 'center',
              fill: 'red',
            }
          }
        ]
        }
      }


        //const dataItems = this.optionData.data
        const values    = dataItems.map(i => Number(i.value))
        //const total  = this.optionData.total != null
        //         ? Number(this.optionData.total)
        //         : values.reduce((sum, v) => sum + v, 0);
  
        // Y-axis ceiling logic
        const maxVal    = Math.max(...values, 0);
        const magnitude = 10 ** Math.floor(Math.log10(maxVal));
        const ratio     = maxVal / magnitude;
        const step = magnitude / 2;
        const headRoom  = (maxVal % step) > (step / 2) ? 1 : 0
        const top =
        ratio <= 2
          ? 2 * magnitude
          : (Math.ceil(ratio) + 1) * magnitude;
  
        // bar gradient
        const barGradient = new echarts.graphic.LinearGradient(
          0,0,0,1,
          [
            { offset: 0,   color: '#A4DEFF' },
            { offset: 0.5, color: '#3AADEE' },
            { offset: 1,   color: '#026BA7' }
          ]
        )
  
        // build the data array for series
        const seriesData = dataItems.map(i => {
          const raw = Number(i.value)
          const pct = total > 0
            ? ((raw/total)*100).toFixed(2)
            : '0.00'
  
          return {
            value: raw.toFixed(3),
            itemStyle: {
              borderRadius: raw >= 0 ? [8,8,0,0] : [0,0,8,8]
            }
          }
        })
  
        return {
          backgroundColor: '#fff',
          title: {
            text: this.optionData.title,
            left: 'center',
            top:  '3%',
            textStyle: {
              color: '#2272FF',
              fontSize: 24,
              fontWeight: 500
            }
          },
          grid: { top: 80, left: 40, right: 40, bottom: 20, containLabel: true },
          tooltip: { trigger: 'axis', axisPointer: { type: 'shadow' } },
          xAxis: {
            type: 'category',
            data: dataItems.map(i => i.name),
            axisLabel: { margin: 10, rotate: shouldRotate ? 45 : 0, interval: 0},
            axisLine:  { lineStyle: { color: '#666' } },
            axisTick:  { alignWithLabel: true }
          },
          yAxis: {
        type: 'value',
        max:      top,
        interval: step,
        axisLine:  { show: false },
        splitLine: { lineStyle: { color: '#eee' } }
        },
          series: [{
            name: this.optionData.title,
            type: 'bar',
            data: seriesData,
            barMaxWidth: '40%',
            itemStyle: { color: barGradient },
            label: {
              show: true,
              align: 'center',
              position: 'top',
              formatter: ({ data }) => {
                // note: params.value already has the 3 decimals as string
                const raw = data.value
                const pct = total > 0
                  ? ((raw/total)*100).toFixed(2)
                  : '0.00'
                return `{valueStyle|${raw}}\n{percentStyle|(${pct}%)}`
              },
              lineHeight: 18,
              rich: {
                valueStyle:   { fontSize: 14, color: '#444', textAlign: 'center', align: 'center' },
                percentStyle: { fontSize: 10, color: '#888', textAlign: 'center', align: 'center' }
              },
            },
            labelLayout: {
              hideOverlap: true,  // Ensures overlap is hidden by default
            },
          }]
        }
      }
    }
  }
  </script>
  
  <style scoped>
  .echart   { width: 100%; overflow: hidden; }
  .barChart { height: 550px; width: 100%; }
  </style>
  